import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { generateCalendarLinks } from '../Utils/CalendarLinks';

const { 
  FiX, FiBell, FiMail, FiCheck, FiClock, 
  FiCalendar, FiShield, FiPhone, FiMessageSquare,
  FiPlus, FiChevronDown, FiExternalLink, FiDownload
} = FiIcons;

const REMINDER_TIMINGS = [
  { id: '3days', label: '3 Days Before', icon: FiCalendar },
  { id: '1day', label: '24 Hours Before', icon: FiCalendar },
  { id: '2hours', label: '2 Hours Before', icon: FiClock },
  { id: '15mins', label: '15 Minutes Before', icon: FiClock },
];

const EventReminderModal = ({ isOpen, onClose, eventTitle, eventDate, eventLocation, eventDescription }) => {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedTimings, setSelectedTimings] = useState(['1day']);
  const [channels, setChannels] = useState(['email']);
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const calendarLinks = generateCalendarLinks({
    title: eventTitle,
    date: eventDate,
    location: eventLocation || 'Virtual Sanctuary',
    description: eventDescription || `Joining the tribute for ${eventTitle}`
  });

  const toggleTiming = (id) => {
    setSelectedTimings(prev => 
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const toggleChannel = (id) => {
    setChannels(prev => 
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        onClose();
      }, 3000);
    }, 1500);
  };

  const downloadIcal = () => {
    const element = document.createElement("a");
    const file = new Blob([calendarLinks.icalData], {type: 'text/calendar'});
    element.href = URL.createObjectURL(file);
    element.download = "memorial-event.ics";
    document.body.appendChild(element);
    element.click();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md overflow-y-auto">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-brand-dark"
      >
        <div className="p-8 md:p-12">
          <div className="flex justify-between items-start mb-10">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-accent-gold/10 rounded-2xl flex items-center justify-center text-accent-gold shadow-sm border border-accent-gold/20">
                <SafeIcon icon={FiBell} className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-3xl font-serif text-accent-navy">Personalize Reminder</h3>
                <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Ensure you're present for the tribute</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full transition-colors text-text-muted">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>

          <AnimatePresence mode="wait">
            {!isSuccess ? (
              <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-10">
                {/* Event Summary Card */}
                <div className="bg-brand-light p-6 rounded-3xl border border-brand-dark flex flex-col md:flex-row justify-between items-center gap-6">
                  <div className="text-center md:text-left">
                    <p className="text-sm text-accent-navy font-bold mb-1">{eventTitle}</p>
                    <p className="text-xs text-text-muted">
                      {new Date(eventDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => window.open(calendarLinks.googleUrl, '_blank')} className="p-3 bg-white hover:bg-accent-gold hover:text-white rounded-xl border border-brand-dark transition-all text-accent-navy flex items-center gap-2 group">
                      <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                      <span className="text-[10px] font-black uppercase">Google</span>
                    </button>
                    <button onClick={downloadIcal} className="p-3 bg-white hover:bg-accent-navy hover:text-white rounded-xl border border-brand-dark transition-all text-accent-navy flex items-center gap-2 group">
                      <SafeIcon icon={FiDownload} className="w-4 h-4" />
                      <span className="text-[10px] font-black uppercase">iCal</span>
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Channel Selection */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block flex justify-between">
                        Contact Methods
                        <span className="text-accent-gold lowercase italic font-light opacity-60">select one or both</span>
                      </label>
                      <div className="space-y-3">
                        <button 
                          type="button" 
                          onClick={() => toggleChannel('email')}
                          className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all ${channels.includes('email') ? 'border-accent-gold bg-brand-light' : 'border-brand-dark opacity-60'}`}
                        >
                          <SafeIcon icon={FiMail} className={channels.includes('email') ? 'text-accent-gold' : ''} />
                          <span className="text-xs font-bold uppercase tracking-widest">Email Alerts</span>
                        </button>
                        <button 
                          type="button" 
                          onClick={() => toggleChannel('sms')}
                          className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all ${channels.includes('sms') ? 'border-accent-gold bg-brand-light' : 'border-brand-dark opacity-60'}`}
                        >
                          <SafeIcon icon={FiPhone} className={channels.includes('sms') ? 'text-accent-gold' : ''} />
                          <span className="text-xs font-bold uppercase tracking-widest">SMS Notifications</span>
                        </button>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">Contact Details</label>
                      <div className="space-y-3">
                        <input 
                          type="email" 
                          placeholder="Email Address" 
                          disabled={!channels.includes('email')}
                          className="w-full px-5 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-xs font-bold disabled:opacity-30 transition-all"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                        <input 
                          type="tel" 
                          placeholder="Phone Number" 
                          disabled={!channels.includes('sms')}
                          className="w-full px-5 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-xs font-bold disabled:opacity-30 transition-all"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Timing Selection */}
                  <div className="space-y-4">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">Notification Frequency</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {REMINDER_TIMINGS.map((opt) => (
                        <button 
                          key={opt.id} 
                          type="button" 
                          onClick={() => toggleTiming(opt.id)}
                          className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all ${selectedTimings.includes(opt.id) ? 'border-accent-gold bg-brand-light' : 'border-brand-dark hover:border-accent-gold/30'}`}
                        >
                          <SafeIcon icon={opt.icon} className={`w-5 h-5 ${selectedTimings.includes(opt.id) ? 'text-accent-gold' : 'text-text-muted'}`} />
                          <span className="text-[9px] font-black uppercase text-center leading-tight">{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Preparation Note */}
                  <div className="space-y-4">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">Preparation Note (Optional)</label>
                    <textarea 
                      placeholder="e.g. Bring white lilies, wear something blue, or prepare a 2-minute reading..."
                      className="w-full px-6 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none transition-all h-28 resize-none text-sm font-light italic"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                  </div>

                  <button 
                    type="submit" 
                    disabled={isSubmitting || (channels.length === 0)}
                    className="w-full btn-primary py-5 text-lg flex items-center justify-center gap-3 shadow-xl shadow-accent-navy/10 relative overflow-hidden group"
                  >
                    <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                    {isSubmitting ? (
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>Activate Reminders</>
                    )}
                  </button>
                </form>
              </motion.div>
            ) : (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="py-16 text-center">
                <div className="w-28 h-28 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-10 shadow-2xl shadow-green-200">
                  <SafeIcon icon={FiCheck} className="w-14 h-14" />
                </div>
                <h4 className="text-4xl font-serif text-accent-navy mb-4">Reminders Set</h4>
                <p className="text-text-muted font-light leading-relaxed max-w-sm mx-auto mb-10">
                  We'll notify you across your selected channels. May this tribute bring peace and beautiful reflection.
                </p>
                <div className="flex justify-center gap-4">
                  <button onClick={onClose} className="btn-secondary px-10">Close</button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="bg-brand-light p-6 flex justify-center items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-text-muted border-t border-brand-dark">
          <SafeIcon icon={FiShield} className="w-4 h-4 text-accent-gold" /> Secure & Respectful Communication
        </div>
      </motion.div>
    </div>
  );
};

export default EventReminderModal;